// Copyright (c) 2026 Goffy. All Rights Reserved.

// Offers a newer Play build from inside the app: Google's own "update available" dialog, a
// background download, then our "restart now?" — never a restart in the middle of a round.

import AsyncStorage from '@react-native-async-storage/async-storage';
// Alert is fine here: offerPlayUpdate returns before it on anything but Android, where it works.
// eslint-disable-next-line no-restricted-imports
import { Alert, Platform } from 'react-native';

import { PlayAppUpdate } from '@/modules/play-app-update';

/** The versionCode the user said no to, so the same build is not offered on every launch. */
const DECLINED_KEY = 'goffy:appUpdate:declinedVersionCode';

export type RestartPromptText = { title: string; message: string; restart: string; later: string };

export type AppUpdateOutcome = 'none' | 'declined' | 'downloading' | 'ready';

type Updater = NonNullable<typeof PlayAppUpdate>;

const askToRestart = (updater: Updater, text: RestartPromptText) => {
  Alert.alert(text.title, text.message, [
    { text: text.later, style: 'cancel' },
    { text: text.restart, onPress: () => void updater.completeAsync().catch(() => undefined) },
  ]);
};

/**
 * Checks Play for a newer build and offers it. Anything that goes wrong — a sideloaded or dev
 * build, no Play Store, no network — ends as 'none': an update prompt is never worth an error.
 */
export async function offerPlayUpdate(
  text: RestartPromptText,
  updater: typeof PlayAppUpdate = PlayAppUpdate
): Promise<AppUpdateOutcome> {
  if (Platform.OS !== 'android' || !updater) return 'none';

  let info;
  try {
    info = await updater.checkAsync();
  } catch {
    return 'none';
  }

  // Downloaded on an earlier launch and not installed yet (the user chose "later").
  if (info.installStatus === 'DOWNLOADED') {
    askToRestart(updater, text);
    return 'ready';
  }
  if (!info.available || !info.flexibleAllowed) return 'none';

  const versionCode = String(info.availableVersionCode);
  if ((await AsyncStorage.getItem(DECLINED_KEY).catch(() => null)) === versionCode) return 'declined';

  const subscription = updater.addListener('onInstallStatus', ({ status }) => {
    if (status === 'DOWNLOADED') askToRestart(updater, text);
    if (status === 'DOWNLOADED' || status === 'FAILED' || status === 'CANCELED') subscription.remove();
  });

  const accepted = await updater.startFlexibleAsync().catch(() => false);
  if (!accepted) {
    subscription.remove();
    await AsyncStorage.setItem(DECLINED_KEY, versionCode).catch(() => undefined);
    return 'declined';
  }
  return 'downloading';
}
